'use client'
import {ButtonSpinner, Form, Input, Locale, TextArea} from '@local/ui/components'
import {cx} from '@local/utils/cx'
import {useRef, useState} from 'react'
import {EMAIL_PATTERN, emailIsValid} from '@local/utils/string'
import {useTranslate} from '@local/hooks'
import PhoneNumberInput from '@local/ui/components/phoneNumberInput/PhoneNumberInput'

interface BecomePartnerFormProps {
	successText: string
	successTitle: string
}

export const BecomePartnerForm = ({
	successText,
	successTitle,
}:BecomePartnerFormProps) => {
	const locale = useTranslate()

	const [inputs, setInputs] = useState({
		venueName: '',
		websiteURL: '',
		capacity: '',
		location: '',
		firstname: '',
		lastname: '',
		email: '',
		describeVenue: '',

	})
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [isSuccess, setIsSuccess] = useState<boolean>(false)
	const [hasError, setHasError] = useState<boolean>(false)

	const formRef = useRef<HTMLFormElement>(null)

	const handleInput = (value:string, name:string) => {
		setInputs((prev) => ({...prev, [name]: value}))
	}

	const handleReset = () => {
		setInputs({
			venueName: '',
			websiteURL: '',
			capacity: '',
			location: '',
			firstname: '',
			lastname: '',
			email: '',
			describeVenue: '',
		})
	}

	const handleSubmit = async ({body}) => {
		setIsLoading(true)
		try {
			setIsSuccess(false)
			setHasError(false)

			if (!emailIsValid(body.email)) {
				throw new Error('InvalidEmail')
			}

			const res = await fetch('/api/partnership/', {
				method: 'POST',
				body: JSON.stringify({
					emailAddress: body.email,
					phoneNumber: body.countryCode + body.phoneNumber,
					firstName: body.firstname,
					lastName: body.lastname,
					message: body.describeVenue,
					companyName: body.venueName,
					websiteURL: body.websiteURL,
				}),
			})
			const result = await res.json()

			if (res.status === 200) {
				setIsSuccess(true)
				setIsLoading(false)
				return {status: 200}
			} else {
				setIsLoading(false)
				setHasError(true)
				return {errors: [result]}
			}
		} catch (error) {
			setIsLoading(false)
			setHasError(true)

			if (error.message === 'InvalidEmail') {
				return {errors: [{code: 400, message: 'Invalid email'}]}
			}

			if (error.message === 'RequestFailed') {
				return {errors: [{code: 500, message: 'Request failed'}]}
			}
		}
	}

	return (
		<div className='max-w-600 flex bg-transparent justify-center items-center self-center  '>

			<Form
				ref={formRef}
				onSubmit={handleSubmit}
				successMessage={<SuccessPanel title={successTitle} text={successText} />}
				errorClassName='bottom-8 text-center'
				additionalReset={handleReset}
				autoReset
				successClassName='z-5 mt-0 -inset-1 bg-white w-[calc(100%+2px)]'
				className={cx(
					'flex flex-col h-fit  w-full pt-5 gap-50 rounded-sm lg:border border-grey-2 px-20 py-10',
					'[&_h3]:font-normal [&_h3]:text-18',
				)}>

				<div className='grid grid-cols-2 gap-10'>

					<Input
						required
						variant='light'
						type='text'
						name='firstname'
						label={locale('Firstname')}
						value={inputs.firstname}
						onChange={e => handleInput(e.target.value, 'firstname')}
						className='h-48'
						inputClassName='h-full'
						errorClassName='z-10 bg-grey-4'
						labelClassName='text-black bg-transparent' />
					<Input
						required
						variant='light'
						type='text'
						name='lastname'
						label={locale('Lastname')}
						value={inputs.lastname}
						onChange={e => handleInput(e.target.value, 'lastname')}
						className='h-48'
						inputClassName='h-full'
						errorClassName='z-10 bg-grey-4'
						labelClassName='text-black bg-transparent' />

					<Input
						required
						variant='light'
						type='email'
						name='email'
						label='Email'
						value={inputs.email}
						onChange={e => handleInput(e.target.value, 'email')}
						onInvalid={() => setHasError(true)}
						pattern={EMAIL_PATTERN}
						hasError={hasError}
						className='h-48 col-span-2'
						inputClassName='h-full'
						errorClassName='z-10 bg-grey-4'
						labelClassName='text-black bg-transparent' />
					<Input
						required
						variant='light'
						type='text'
						name='venueName'
						label={locale('Company name')}
						value={inputs.venueName}
						onChange={e => handleInput(e.target.value, 'venueName')}
						className='h-48 col-span-2'
						inputClassName='h-full'
						errorClassName='z-10 bg-grey-4'
						labelClassName='text-black bg-transparent' />
					<Input
						required
						variant='light'
						type='text'
						name='websiteURL'
						label={locale('Website URL')}
						value={inputs.websiteURL}
						onChange={e => handleInput(e.target.value, 'websiteURL')}
						className='h-48 col-span-2'
						inputClassName='h-full'
						errorClassName='z-10 bg-grey-4'
						labelClassName='text-black bg-transparent' />
					{/* <Input
						required
						variant='light'
						type='text'
						name='capacity'
						label={locale('Venue Capacity')}
						value={inputs.capacity}
						onChange={e => handleInput(e.target.value, 'capacity')}
						className='h-48 col-span-2'
						max={9999}
						min={0}
						inputClassName='h-full'
						errorClassName='z-10 bg-grey-4'
						labelClassName='text-black bg-transparent' /> */}

					<PhoneNumberInput
						onChange={(value:string) => handleInput(value, 'phoneNumber')}
						onInvalid={() => setHasError(true)}
						light
						hasError={hasError} />

					<TextArea
						required
						variant='light'
						name='describeVenue'
						label={locale('What unique value can your brand bring to a partnership with Kaer?')}
						inputClassName='h-full'
						className='h-72 col-span-2'
						errorClassName='z-10 bg-grey-4'
						labelClassName='text-black bg-transparent' />

				</div>

				<div className='w-full'>
					<ButtonSpinner
						type='submit'
						small
						loading={isLoading}
						variant='primary'
						className='mt-auto w-full'>
						<Locale>Send</Locale>
					</ButtonSpinner>
				</div>
			</Form>
		</div>

	)
}

const SuccessPanel = ({title, text}) => (
	<div className='flex flex-col bg-grey-1 items-center text-center  w-full h-fit gap-14  normal-case'>
		<h3 className='text-32 text-green-1 font-serif tracking-tight'>{title}</h3>
		<p className='text-12  '>{text}</p>
	</div>
)
